// One manager's trophy room (AWARDS.md "The trophy room"): the plaque, a
// shelf per season with trophies and medals, and the badge wall. Every
// other manager's room is one chip away; Share draws the card.

import { useState } from 'react';
import { Button, Caption, Text } from '../../kit/ui';
import type { MilestoneView, TrophyRoomView } from '@fsp/domain';
import { useTrophyRoom } from '../../data/hooks';
import { useTier } from '../../shell/useTier';
import { TeamMark } from '../../kit';
import { DetailSheet } from '../../templates/DetailSheet';
import { AwardIcon, Badge, BadgeDetail, Shelf, TitleCrown, TrophyTile } from './AwardKit';
import { SharePanel } from './AwardKit';
import { FromLeague } from './FromLeague';
import { TeamName } from './Names';
import { useTried } from '../../data/tried';

export function TrophyRoom({
  teamId,
  onPick,
}: {
  teamId: string;
  onPick: (teamId: string) => void;
}) {
  // A real league's room is a read (PLAN-2026-09-26-season-end S4).
  return (
    <FromLeague snapshot={useTrophyRoom(teamId)}>
      {(room) => <Room room={room} teamId={teamId} onPick={onPick} />}
    </FromLeague>
  );
}

function Room({
  room,
  teamId,
  onPick,
}: {
  room: TrophyRoomView;
  teamId: string;
  onPick: (teamId: string) => void;
}) {
  const tier = useTier();
  // Somebody else's room, for the activity log (A2); my own is not a visit.
  useTried('trophy-room', !room.isMine);
  const [badge, setBadge] = useState<MilestoneView | null>(null);
  const [share, setShare] = useState(false);
  const earned = room.milestones.filter((a) => a.tier !== null);
  const locked = room.milestones
    .filter((a) => a.tier === null)
    .sort((a, b) => b.progress - a.progress);
  const medalsTotal = room.seasons.reduce(
    (n, s) => n + s.medals.reduce((m, x) => m + x.count, 0),
    0,
  );
  const phone = tier === 'phone';
  const possessive = room.isMine ? 'My' : `${room.owner}'s`;

  return (
    <>
      <div className="fsp-managers" role="tablist" aria-label="Trophy rooms">
        {room.managers.map((m) => (
          <button
            key={m.id}
            type="button"
            className="fsp-manager"
            aria-pressed={m.id === teamId}
            onClick={() => onPick(m.id)}
          >
            <TeamMark team={m} size={20} />
            {m.isMine ? 'Me' : m.ownerName}
            {m.titles ? <TitleCrown titles={m.titles} /> : null}
          </button>
        ))}
      </div>
      <div className="fsp-room">
        <div>
          <div className="fsp-room-plaque">
            <TeamMark team={room.team} size={44} />
            <div className="fsp-room-plaque-body">
              <div className="fsp-room-plaque-name fsp-disp">
                {/* The room's team, one tap from its roster (B-220). */}
                <span className="fsp-name-text">
                  <TeamName team={room.team} />
                </span>
                {room.team.titles ? <TitleCrown titles={room.team.titles} /> : null}
              </div>
              <div className="fsp-sub fsp-ui-wrap-text">
                {possessive} trophy room · {room.headline}
              </div>
            </div>
            <Button size="xs" icon="share" onClick={() => setShare(true)}>
              Share
            </Button>
          </div>
          {room.seasons.map((s) => (
            <Shelf
              key={s.season}
              title={
                <>
                  <span>{s.season}</span>
                  <span className="fsp-sub">
                    {s.finish} · {s.record}
                  </span>
                </>
              }
            >
              {s.trophies.length ? (
                s.trophies.map((t) => (
                  <TrophyTile
                    key={t.awardId}
                    trophy={t}
                    team={room.team}
                    season={s.season}
                    trophyName={room.trophyName}
                  />
                ))
              ) : (
                <Text tone="secondary">
                  {s.isCurrent
                    ? 'Nothing on the shelf yet. The season decides.'
                    : 'No trophies that year.'}
                </Text>
              )}
              {s.medals.length ? (
                <div className="fsp-medals fsp-room-full">
                  {s.medals.map((m) => (
                    <span key={m.awardId} className="fsp-medal" data-tone={m.tone} title={m.name}>
                      <AwardIcon icon={m.icon} size={14} />
                      {m.name} ×{m.count}
                      {m.careerCount > m.count ? (
                        <span className="fsp-sub">({m.careerCount} career)</span>
                      ) : null}
                    </span>
                  ))}
                </div>
              ) : s.isCurrent ? (
                <span className="fsp-room-full">
                  <Text tone="secondary" size="xs">
                    No weekly awards yet.
                  </Text>
                </span>
              ) : null}
            </Shelf>
          ))}
        </div>
        <div>
          <div className="fsp-region-head fsp-room-badges-head">
            <Caption>Milestones</Caption>
            <Caption>
              {earned.length} of {room.milestones.length}
            </Caption>
          </div>
          <div className="fsp-badges">
            {[...earned, ...locked].map((a) => (
              <Badge key={a.id} a={a} onOpen={setBadge} />
            ))}
          </div>
        </div>
      </div>
      <BadgeDetail a={badge} onClose={() => setBadge(null)} />
      <DetailSheet opened={share} onClose={() => setShare(false)} title="Share the room">
        <SharePanel
          input={{
            kicker: 'Trophy room',
            headline: room.team.name,
            subline: `${room.owner} · ${room.league.name}`,
            line: roomLine(room, medalsTotal, earned.length),
            footer: `${room.league.name} · ${room.league.season}`,
            team: room.team,
            tone: 'glory',
          }}
          text={`${room.shareText} ${roomLine(room, medalsTotal, earned.length)}`}
        />
        {phone ? null : (
          <div className="fsp-room-note">
            <Text tone="secondary" size="xs">
              The image is drawn from this room as it stands.
            </Text>
          </div>
        )}
      </DetailSheet>
    </>
  );
}

function roomLine(room: TrophyRoomView, medals: number, badges: number): string {
  const c = room.career;
  const parts: string[] = [];
  parts.push(c.titles ? `${c.titles} title${c.titles === 1 ? '' : 's'}` : 'No titles yet');
  parts.push(`${medals} weekly award${medals === 1 ? '' : 's'}`);
  parts.push(`${badges} milestone${badges === 1 ? '' : 's'}`);
  return `${parts.join(', ')}. ${c.bestWeek ? `Best week: ${c.bestWeek.value.toFixed(1)}.` : ''}`.trim();
}
